/**
 * semantic-landing-translator.ts
 *
 * Full 7-Section Multi-Lingual Projection Engine (Sprint CMS-STUDIO-DEEP-TRANSLATION-MEDIA-04):
 * - Immutable localization projection covering all 7 sections (Hero, Comparison, 5 Service Pillars, 4 Tech Engine items, 6 Industry sectors, 4 Trust Governance cards, Lead Capture & HQ).
 * - Supported Locales: en-US, sw-KE, lg-UG, fr-FR, ar-SA (RTL), zh-CN (CJK).
 */

import type {
  CmsPageTree,
  SupportedLocale,
  CmsHeroBannerBlock,
  CmsComparisonMatrixBlock,
  CmsServiceCatalogBlock,
  CmsTechEngineBlock,
  CmsIndustryMatrixBlock,
  CmsTrustAuditBlock,
  CmsLeadCaptureFormBlock,
} from '../cms-contracts.js';

export interface LocalizedSectionCopy {
  readonly hero: {
    readonly eyebrow: string;
    readonly title: string;
    readonly description: string;
    readonly primaryCta: string;
    readonly secondaryCta: string;
    readonly badges: readonly string[];
  };
  readonly comparison: {
    readonly heading: string;
    readonly subheading: string;
    readonly legacyTitle: string;
    readonly legacyPoints: readonly string[];
    readonly modernTitle: string;
    readonly modernPoints: readonly string[];
  };
  readonly services: {
    readonly heading: string;
    readonly subheading: string;
    readonly items: readonly {
      readonly serviceId: string;
      readonly targetAudience: string;
      readonly title: string;
      readonly description: string;
      readonly features: readonly string[];
    }[];
  };
  readonly tech: {
    readonly heading: string;
    readonly coreMessage: string;
    readonly items: readonly {
      readonly metricBadge: string;
      readonly title: string;
      readonly description: string;
      readonly bullets: readonly string[];
    }[];
  };
  readonly industry: {
    readonly heading: string;
    readonly subheading: string;
    readonly items: readonly {
      readonly name: string;
      readonly risk: string;
      readonly tags: readonly string[];
    }[];
  };
  readonly trust: {
    readonly heading: string;
    readonly subheading: string;
    readonly items: readonly {
      readonly standard: string;
      readonly title: string;
      readonly description: string;
    }[];
  };
  readonly lead: {
    readonly heading: string;
    readonly description: string;
    readonly officeTitle: string;
    readonly officeLocation: string;
    readonly officePhone: string;
    readonly officeEmail: string;
    readonly officeHours: string;
    readonly labels: {
      readonly name: string;
      readonly company: string;
      readonly email: string;
      readonly phone: string;
      readonly areaOfInquiry: string;
      readonly scopeNotes: string;
      readonly consent: string;
      readonly submit: string;
    };
    readonly options: readonly string[];
  };
}

export const SEVEN_SECTION_LOCALIZATIONS: Record<SupportedLocale, LocalizedSectionCopy> = {
  'en-US': {
    hero: {
      eyebrow: 'Institutional Security & Asset Protection',
      title: 'Protecting Critical Assets. Securing Uganda’s Future.',
      description: 'From high-security private vaults and armored bullion logistics to 24/7 AI-assisted SOC surveillance and industrial protection—engineered for Uganda’s expanding economy.',
      primaryCta: 'Request Security Assessment',
      secondaryCta: 'Explore Private Vaults',
      badges: ['Bank of Uganda Compliant', 'ISO 27001 Certified SOC', 'Class-4 Modular Vaults', 'B6/B7 Armored Fleet'],
    },
    comparison: {
      heading: 'The Paradigm Shift in Asset Defense',
      subheading: 'Why modern institutions choose proactive technology-driven security over traditional static guarding.',
      legacyTitle: 'Traditional Security Model',
      legacyPoints: [
        'Static guards with limited real-time communication',
        'Unverified manual incident logbooks prone to tampering',
        'Vulnerable soft-skin vehicles for cash and bullion transport',
        'Delayed emergency response during coordinated attacks',
      ],
      modernTitle: 'Integrated Security Platform',
      modernPoints: [
        'Centralized SOC dispatch with sub-100ms video telemetry',
        'Immutable cryptographic audit trails for every access event',
        'B6/B7 ballistic armored fleet with dynamic route risk AI',
        'Multi-agency coordinated tactical armed response teams',
      ],
    },
    services: {
      heading: 'Five Strategic Service Pillars',
      subheading: 'Comprehensive institutional asset defense and specialized security infrastructure for East Africa.',
      items: [
        {
          serviceId: 'pillar-custody',
          targetAudience: 'Mining, Banks & Estates',
          title: 'Secure Custody & Private Vaults',
          description: 'Class-4 modular vaults in Kampala featuring biometric dual-custody access, climate control, and comprehensive bullion insurance.',
          features: ['Biometric & Dual-Key Custody', '24/7 Sensor Monitoring', 'Full Underwriting Coverage', 'Confidential Safe Deposit Lockers'],
        },
        {
          serviceId: 'pillar-cit',
          targetAudience: 'Refineries, Retail & Financial',
          title: 'Cash-in-Transit (CIT) & Bullion Logistics',
          description: 'Armored cash, foreign exchange, and precious metal transport throughout Kampala, Entebbe, and regional mining corridors.',
          features: ['B6/B7 Ballistic Armored Fleet', 'Real-Time GPS Route Optimization', 'Armed Tactical Escort Detail', 'Inter-Vault Sealed Air Transfer'],
        },
        {
          serviceId: 'pillar-soc',
          targetAudience: 'Industrial & Critical Infrastructure',
          title: 'AI-Assisted Surveillance & Smart Tech',
          description: 'Centralized 24/7 Security Operations Centre (SOC) integrating edge AI cameras, perimeter thermal sensors, and automated alarms.',
          features: ['AI Edge Anomaly Detection', 'Thermal Perimeter Defense', 'Under-100ms Alert Telemetry', 'Automated Police/SOC Dispatch'],
        },
        {
          serviceId: 'pillar-industry',
          targetAudience: 'Mining Concessions & Oil Infrastructure',
          title: 'High-Value Industry Security',
          description: 'Turnkey physical and electronic defense architecture customized for gold mines, refineries, and heavy manufacturing plants.',
          features: ['Perimeter Anti-Intrusion Grids', 'Access Control Checkpoints', 'On-Site Rapid Tactical Response', 'Supply Chain Loss Prevention'],
        },
        {
          serviceId: 'pillar-audit',
          targetAudience: 'Corporates, Embassies & Investors',
          title: 'Threat Assessment & Risk Advisory',
          description: 'Senior intelligence evaluations, vulnerability audits, physical penetration testing, and executive protection strategy.',
          features: ['Site Vulnerability Audits', 'Executive Route Profiling', 'Crisis Management Architecture', 'Regulatory Compliance Advisory'],
        },
      ],
    },
    tech: {
      heading: 'Security Engine & AI Telemetry',
      coreMessage: 'Human Intelligence + Artificial Intelligence = Smarter, Faster Protection',
      items: [
        {
          metricBadge: '< 2.4s Incident Detection',
          title: 'AI Video Edge & Threat Isolation',
          description: 'Edge cameras detect loitering, perimeter crossing, and unauthorized vehicle stopping before a breach occurs.',
          bullets: ['Sub-100ms video telemetry streaming', 'Automated weapon & silhouette detection', 'Instant SOC alarm escalation'],
        },
        {
          metricBadge: '100% Real-Time Fleet Telemetry',
          title: 'CIT & Asset Telemetry GPS',
          description: 'Continuous telemetry on armored vehicle speed, door latch states, engine health, and geofence deviation.',
          bullets: ['Dynamic route risk assessment', 'Remote engine disable interlock', 'Duress panic triggers with rapid response'],
        },
        {
          metricBadge: 'Zero Single-Point Access',
          title: 'Biometric Access & Dual-Custody',
          description: 'Vault access requires simultaneous biometric authorization from two certified custodians and central SOC clearance.',
          bullets: ['Dual-custody biometric multi-factor authentication', 'Cryptographic access logs on tamper-evident ledger', 'Time-lock interlock mechanisms'],
        },
        {
          metricBadge: '24/7 Continuous Readiness',
          title: '24/7 Command SOC Dispatch',
          description: 'Centralized command and control hub coordinating fixed site alarms, mobile escorts, and armed tactical teams.',
          bullets: ['Direct hotline to Uganda Police & private response', 'Redundant satellite and cellular failover', 'Full audit playback and incident debriefing'],
        },
      ],
    },
    industry: {
      heading: 'Specialized Defense for High-Risk Sectors',
      subheading: 'Tailored security architectures engineered for specific vulnerability profiles across Uganda and East Africa.',
      items: [
        {
          name: 'Mining & Gold Refineries',
          risk: 'High risk of armed robbery, internal theft, and transport ambush during cross-border transit.',
          tags: ['Bullion Custody', 'Armored CIT Logistics', 'Thermal Perimeter Sensors'],
        },
        {
          name: 'Oil & Gas Sector',
          risk: 'Critical pipeline infrastructure, remote rig sites, and hazardous supply chain logistics.',
          tags: ['Perimeter Anti-Intrusion', 'Drone Surveillance', 'Remote SOC Telemetry'],
        },
        {
          name: 'Banking & Financial Institutions',
          risk: 'Branch robbery, cash processing vulnerability, and overnight vault exposure.',
          tags: ['Daily Cash Escort', 'Modular Class-4 Vaults', 'Dual-Custody Lockers'],
        },
        {
          name: 'Infrastructure & EPC Contractors',
          risk: 'Heavy machinery theft, remote camp vulnerability, and supply depot intrusion.',
          tags: ['24/7 Guarded Compounds', 'Vehicle GPS Trackers', 'Perimeter AI Cameras'],
        },
        {
          name: 'Corporate, Embassies & Legal',
          risk: 'Corporate espionage, kidnapping risk, and confidential document vulnerability.',
          tags: ['Executive Protection', 'Threat Risk Advisory', 'Secure Safe Deposit'],
        },
        {
          name: 'Private Clients & Family Estates',
          risk: 'Residential home invasion, estate vulnerability, and family asset exposure.',
          tags: ['Private Safe Custody', 'Panic Room Integration', 'Rapid Alarm Response'],
        },
      ],
    },
    trust: {
      heading: 'Trust Is Our Primary Asset.',
      subheading: 'When protecting land titles, national infrastructure, or corporate treasury, trust must be structural, auditable, and absolute.',
      items: [
        {
          standard: 'Personnel Vetting Standard',
          title: 'Rigorous Background Vetting',
          description: 'Every security officer, vault custodian, and tactical driver undergoes continuous biometric, criminal, and financial background screening.',
        },
        {
          standard: 'Custodial Protocol Standard',
          title: 'Strict Chain of Custody',
          description: 'All gold, currency, and high-value documents receive tamper-evident serialization, dual-custody handoffs, and digital cryptographic logging.',
        },
        {
          standard: 'Institutional Compliance Standard',
          title: 'Confidentiality by Design',
          description: 'Bank-grade non-disclosure agreements, air-gapped data retention, and strict physical access compartmentalization.',
        },
        {
          standard: 'Regulatory Licensing Standard',
          title: 'Fully Licensed & Regulated',
          description: 'Certified by the Ministry of Internal Affairs and compliant with Bank of Uganda high-value asset transit guidelines.',
        },
      ],
    },
    lead: {
      heading: 'Secure Your Operations & Assets',
      description: 'Speak with our Kampala-based security and asset custody specialists for a confidential risk evaluation.',
      officeTitle: 'Kampala Headquarters & Central SOC',
      officeLocation: 'Plot 42 Nakasero Hill Road, Kampala, Uganda',
      officePhone: '+256 414 000 000',
      officeEmail: 'inquiries@sovereign-security.ug',
      officeHours: '24/7 Security Operations Centre (SOC)',
      labels: {
        name: 'Full Name *',
        company: 'Company / Institution',
        email: 'Official Email Address *',
        phone: 'Direct Phone Number *',
        areaOfInquiry: 'Primary Area of Inquiry *',
        scopeNotes: 'Scope of Operations / Special Requirements',
        consent: 'I consent to confidential security processing in accordance with national data protection regulations.',
        submit: 'Request Confidential Assessment',
      },
      options: [
        'Private Vault Custody & Safe Deposit',
        'Cash-in-Transit (CIT) & Bullion Transport',
        'AI Surveillance & Smart SOC Monitoring',
        'Mining & Industrial Concession Defense',
        'Threat Assessment & Advisory Audit',
      ],
    },
  },

  'sw-KE': {
    hero: {
      eyebrow: 'Ulinzi wa Rasilimali za Kitaasisi',
      title: 'Kulinda Rasilimali Muhimu. Kulinda Mustakabali wa Uganda.',
      description: 'Kuanzia hifadhi ya vyumba vya siri vya benki hadi usafirishaji wa dhahabu (CIT) na ulinzi wa kamera za AI wa saa 24 nchini Uganda.',
      primaryCta: 'Omba Tathmini ya Usalama',
      secondaryCta: 'Gundua Vyumba vya Siri',
      badges: ['Kuzingatia Benki Kuu ya Uganda', 'Kituo cha SOC Kilichoidhinishwa na ISO', 'Vyumba vya Siri vya Daraja la 4', 'Magari ya Kijeshi ya B6/B7'],
    },
    comparison: {
      heading: 'Mabadiliko ya Msingi katika Ulinzi wa Rasilimali',
      subheading: 'Kwanini taasisi za kisasa zinachagua usalama unaoendeshwa na teknolojia badala ya walinzi wa kitamaduni.',
      legacyTitle: 'Mfumo wa Kizamani wa Usalama',
      legacyPoints: [
        'Walinzi waliotulia wasio na mawasiliano ya haraka',
        'Vitabu vya kumbukumbu vinavyoweza kubadilishwa kwa urahisi',
        'Magari ya kawaida yasiyo na ulinzi wa risasi kwa kusafirisha fedha',
        'Ucheleweshaji wa kutoa msaada wakati wa mashambulizi',
      ],
      modernTitle: 'Jukwaa la Kisasa la Usalama',
      modernPoints: [
        'Kituo cha amri cha SOC chenye video ya moja kwa moja chini ya 100ms',
        'Kumbukumbu za kidijitali zisizoweza kufutwa kwa kila kuingia',
        'Magari ya kijeshi ya B6/B7 yanayoongozwa na akili bandia ya njia salama',
        'Vikosi vya dharura vyenye silaha vilivyoratibiwa na polisi',
      ],
    },
    services: {
      heading: 'Nguzo Tano za Huduma za Kimkakati',
      subheading: 'Ulinzi kamili wa rasilimali za kitaasisi na miundombinu maalum ya usalama Afrika Mashariki.',
      items: [
        {
          serviceId: 'pillar-custody',
          targetAudience: 'Madini, Benki na Mashamba Makubwa',
          title: 'Hifadhi Salama na Vyumba vya Siri',
          description: 'Vyumba vya siri vya daraja la 4 mjini Kampala vyenye ufunguo wa kibayometriki wa watu wawili na bima kamili.',
          features: ['Ulinzi wa Kibayometriki na Funguo Mbili', 'Ufuatiliaji wa Saa 24', 'Bima Kamili ya Rasilimali', 'Masanduku ya Siri ya Hifadhi'],
        },
        {
          serviceId: 'pillar-cit',
          targetAudience: 'Usafishaji Dhahabu na Sekta ya Fedha',
          title: 'Usafirishaji Salama wa Fedha na Dhahabu (CIT)',
          description: 'Usafirishaji wa fedha na madini ya thamani kwa magari ya kivita Kampala, Entebbe, na maeneo ya uchimbaji madini.',
          features: ['Magari ya Kivita ya B6/B7', 'Ufuatiliaji wa GPS wa Moja kwa Moja', 'Walinzi Wenye Silaha Nzito', 'Uhamisho Salama wa Ndege'],
        },
        {
          serviceId: 'pillar-soc',
          targetAudience: 'Viwanda na Miundombinu Muhimu',
          title: 'Ulinzi wa Kamera za AI na Kituo cha SOC',
          description: 'Kituo cha amri cha saa 24 kinachounganisha kamera za AI, vitambuzi vya joto, na kengele za dharura.',
          features: ['Utambuzi wa Haraka wa Matishio wa AI', 'Ulinzi wa Mipaka kwa Vitambuzi vya Joto', 'Mawasiliano ya Haraka chini ya 100ms', 'Msaada wa Haraka wa Polisi'],
        },
        {
          serviceId: 'pillar-industry',
          targetAudience: 'Migodi na Miradi ya Mafuta',
          title: 'Ulinzi wa Viwanda vya Thamani Kubwa',
          description: 'Ulinzi kamili wa kielektroniki na wa kimwili ulioundwa maalum kwa migodi ya dhahabu na viwanda vikubwa.',
          features: ['Ulinzi Imara wa Mipaka', 'Vituo vya Ukaguzi wa Kibayometriki', 'Kikosi cha Dharura cha Eneo', 'Kuzuia Upotevu wa Rasilimali'],
        },
        {
          serviceId: 'pillar-audit',
          targetAudience: 'Balozi, Mashirika na Wawekezaji',
          title: 'Tathmini ya Hatari na Ushauri wa Usalama',
          description: 'Tathmini ya kiwango cha juu ya usalama, ukaguzi wa udhaifu wa majengo, na mikakati ya ulinzi wa viongozi.',
          features: ['Ukaguzi wa Usalama wa Majengo', 'Uchambuzi wa Njia Salama', 'Usimamizi wa Dharura', 'Kuzingatia Sheria za Serikali'],
        },
      ],
    },
    tech: {
      heading: 'Mfumo wa Kiteknolojia na Ufuatiliaji wa AI',
      coreMessage: 'Akili ya Kibinadamu + Akili Bandia = Ulinzi Bora na wa Haraka',
      items: [
        {
          metricBadge: '< Sekunde 2.4 Kugundua Hatari',
          title: 'Kamera za AI na Kutambua Matishio',
          description: 'Kamera za AI hutambua watu wanaozunguka ovyo na magari yasiyoruhusiwa kabla ya uvamizi kutokea.',
          bullets: ['Utumaji wa video wa haraka chini ya 100ms', 'Utambuzi wa silaha na maumbo ya watu', 'Kengele ya haraka kwa kituo cha SOC'],
        },
        {
          metricBadge: 'Ufuatiliaji wa GPS wa 100%',
          title: 'Ufuatiliaji wa Magari ya CIT na GPS',
          description: 'Ufuatiliaji wa moja kwa moja wa kasi ya magari ya kivita, milango, na mkengeuko wa njia zilizopangwa.',
          bullets: ['Tathmini ya hatari ya njia kwa wakati halisi', 'Kuzima injini kwa mbali wakati wa hatari', 'Kitufe cha dharura chenye majibu ya haraka'],
        },
        {
          metricBadge: 'Ufikiaji Bila Udhaifu Mmoja',
          title: 'Ulinzi wa Kibayometriki wa Watu Wawili',
          description: 'Kuingia chumbani kunahitaji uthibitisho wa alama za vidole kutoka kwa maafisa wawili na kibali cha SOC.',
          bullets: ['Uthibitishaji wa kibayometriki wa watu wawili', 'Kumbukumbu za kidijitali zisizoweza kubadilishwa', 'Kufunga kwa wakati uliopangwa'],
        },
        {
          metricBadge: 'Utayari Kamili wa Saa 24/7',
          title: 'Kituo Kikuu cha Amri cha SOC',
          description: 'Kituo kikuu cha uratibu wa kengele za majengo, magari ya dharura, na vikosi vyenye silaha.',
          bullets: ['Mawasiliano ya moja kwa moja na Polisi ya Uganda', 'Mawasiliano ya setilaiti yasiyokatika', 'Uchunguzi wa kina wa video za matukio'],
        },
      ],
    },
    industry: {
      heading: 'Ulinzi Maalum kwa Sekta Zenye Hatari Kubwa',
      subheading: 'Mifumo ya usalama iliyoundwa kwa ajili ya mazingira maalum ya Uganda na Afrika Mashariki.',
      items: [
        {
          name: 'Migodi na Usafishaji Dhahabu',
          risk: 'Hatari ya uvamizi wa silaha na wizi wakati wa kusafirisha madini kuvuka mipaka.',
          tags: ['Hifadhi ya Dhahabu', 'Usafirishaji wa Kivita wa CIT', 'Vitambuzi vya Joto vya Mipaka'],
        },
        {
          name: 'Sekta ya Mafuta na Gesi',
          risk: 'Miundombinu ya mabomba ya mafuta na kambi zilizopo maeneo ya mbali.',
          tags: ['Ulinzi wa Mipaka', 'Droni za Ulinzi', 'Ufuatiliaji wa Kituo cha SOC'],
        },
        {
          name: 'Benki na Taasisi za Fedha',
          risk: 'Uvamizi wa matawi ya benki na hatari ya vyumba vya siri nyakati za usiku.',
          tags: ['Usindikizaji wa Fedha', 'Vyumba vya Siri vya Daraja la 4', 'Masanduku ya Siri'],
        },
        {
          name: 'Miradi Mikubwa ya Ujenzi (EPC)',
          risk: 'Wizi wa mitambo mikubwa na uvamizi wa maghala ya vifaa.',
          tags: ['Kambi Zenye Ulinzi wa Saa 24', 'Ufuatiliaji wa GPS wa Magari', 'Kamera za AI za Mipaka'],
        },
        {
          name: 'Mashirika, Balozi na Wanasheria',
          risk: 'Udukuzi wa siri za kibiashara na hatari kwa viongozi wakuu.',
          tags: ['Ulinzi wa Viongozi', 'Ushauri wa Usalama', 'Hifadhi Salama ya Nyaraka'],
        },
        {
          name: 'Wateja Binafsi na Makazi ya Kifahari',
          risk: 'Uvamizi wa nyumbani na hatari kwa mali za kifamilia.',
          tags: ['Hifadhi ya Siri Binafsi', 'Vyumba Maalum vya Usalama', 'Kengele za Msaada wa Haraka'],
        },
      ],
    },
    trust: {
      heading: 'Kuaminika Ndio Rasilimali Yetu Kuu.',
      subheading: 'Wakati wa kulinda hati miliki za ardhi au hazina za benki, uaminifu lazima uwe imara na unaoweza kukaguliwa.',
      items: [
        {
          standard: 'Kiwango cha Ukaguzi wa Wafanyakazi',
          title: 'Ukaguzi Mkali wa Wafanyakazi',
          description: 'Kila afisa wa usalama na dereva hupitia ukaguzi endelevu wa alama za vidole, maadili, na historia ya uhalifu.',
        },
        {
          standard: 'Kiwango cha Ulinzi wa Rasilimali',
          title: 'Msururu Salama wa Makabidhiano',
          description: 'Dhahabu na fedha zote hupewa nambari maalum, makabidhiano ya watu wawili, na kumbukumbu za kidijitali.',
        },
        {
          standard: 'Kiwango cha Usiri wa Taasisi',
          title: 'Usiri Kamili wa Taarifa',
          description: 'Mikataba madhubuti ya kisheria ya kutotoa siri na mifumo salama ya kuhifadhi kumbukumbu bila mtandao.',
        },
        {
          standard: 'Kiwango cha Leseni za Kiserikali',
          title: 'Imeidhinishwa Kikamilifu Kisheria',
          description: 'Imepewa leseni na Wizara ya Mambo ya Ndani na inafuata miongozo ya Benki Kuu ya Uganda.',
        },
      ],
    },
    lead: {
      heading: 'Linda Shughuli na Rasilimali Zako',
      description: 'Zungumza na wataalamu wetu wa usalama mjini Kampala kwa tathmini ya siri ya hatari za kiusalama.',
      officeTitle: 'Makao Makuu ya Kampala na Kituo cha SOC',
      officeLocation: 'Plot 42 Nakasero Hill Road, Kampala, Uganda',
      officePhone: '+256 414 000 000',
      officeEmail: 'inquiries@sovereign-security.ug',
      officeHours: 'Kituo cha Saa 24 cha Ulinzi (SOC)',
      labels: {
        name: 'Jina Kamili *',
        company: 'Kampuni / Taasisi',
        email: 'Barua Pepe Rasmi *',
        phone: 'Nambari ya Simu *',
        areaOfInquiry: 'Eneo Kuu la Huduma Unayohitaji *',
        scopeNotes: 'Maelezo ya Mahitaji Maalum',
        consent: 'Ninakubali usindikaji wa taarifa zangu kwa mujibu wa sheria za ulinzi wa taarifa binafsi.',
        submit: 'Omba Tathmini ya Siri ya Usalama',
      },
      options: [
        'Hifadhi ya Vyumba vya Siri vya Benki',
        'Usafirishaji wa Kivita wa Fedha na Dhahabu (CIT)',
        'Ulinzi wa Kamera za AI na Kituo cha SOC',
        'Ulinzi wa Migodi na Viwanda Vikubwa',
        'Tathmini ya Hatari na Ushauri wa Usalama',
      ],
    },
  },

  'lg-UG': {
    hero: {
      eyebrow: 'Obukuumi bw’Eby’Obugagga Eby’Ebiseera eby’omu Maaso',
      title: 'Okukuuma Eby’Obugagga Ebikulu. Okutebenkeza Ebiseera by’e Ggwanga.',
      description: 'Okuva ku masanduuko g’ebyama ag’omulembe okutuuka ku mmotoka ezitambuza sente ne zaabu (CIT) n’obukuumi bwa kamera za AI obwa saawa 24 mu Uganda.',
      primaryCta: 'Saba Okwekenneenya Obukuumi',
      secondaryCta: 'Laba Amasanduuko g’Ebyama',
      badges: ['Empisa z’Enkola eza Bank of Uganda', 'Kamera za ISO 27001 SOC', 'Amasanduuko ag’Omulembe ogwa Class-4', 'Emmotoka ezitamenyeka eza B6/B7'],
    },
    comparison: {
      heading: 'Enkyukakyuka mu Kukuuma Eby’Obugagga',
      subheading: 'Lwaki ebitongole eby’omulembe bironda tekinologiya okusinga abakuumi aba bulijjo.',
      legacyTitle: 'Enkola ey’Edda ey’Obukuumi',
      legacyPoints: [
        'Abakuumi abayimiridde awo abatalina mpuliziganya ya mangu',
        'Ebitabo ebiwandiikibwamu ebisobola okukyusibwakyusibwa',
        'Emmotoka eza bulijjo ezisobola okukubibwa amasasi nga zitambuza sente',
        'Okulwawo okutuukawo nga wabaddewo obulumbaganyi',
      ],
      modernTitle: 'Enkola Ey’Omulembe Ey’Obukuumi',
      modernPoints: [
        'Kamera ezikwatagana n’ekitebe ekikulu mu butikitiki butono ddala',
        'Kamera n’ebyuma ebikwata buli muntu ayingira awatali kusangulawo',
        'Emmotoka ezitamenyeka eza B6/B7 ezikozesa obwengu obulonda enguudo ensaamu',
        'Abasirikale abalina ebyokulwanyisa abatuukirawo amangwago',
      ],
    },
    services: {
      heading: 'Empagi Ttaano Ez’Obukuumi Obw’Omulembe',
      subheading: 'Obukuumi obujjuvu obw’eby’obugagga n’ebizimbe eby’omulembe mu Buvanjuba bwa Afrika.',
      items: [
        {
          serviceId: 'pillar-custody',
          targetAudience: 'Eby’Ebyobugagga eby’omu Ttaka, Bbanka n’Amaka Amanene',
          title: 'Amasanduuko g’Ebyama n’Obukuumi Obujjuvu',
          description: 'Amasanduuko g’ebyama mu Kampala agaggulwawo n’engalo z’abantu babiri n’obuyambi bwa yinsuwa ejjuvu.',
          features: ['Okuggula n’Engalo z’Abantu Babiri', 'Okukuba Kamera Saawa 24', 'Yinsuwa Ejjuvu ey’Eby’Obugagga', 'Amasanduuko g’Ebyama Ag’Obwannannyini'],
        },
        {
          serviceId: 'pillar-cit',
          targetAudience: 'Abalongoosa Zaabu ne Bbanka',
          title: 'Emmotoka Ezitambuza Sente ne Zaabu (CIT)',
          description: 'Okutambuza sente n’ebintu eby’omuwendo mu mmotoka ezitamenyeka mu Kampala, Entebbe, n’ebifo by’eby’obugagga eby’omu ttaka.',
          features: ['Emmotoka Ezitamenyeka eza B6/B7', 'Okulondoola Emmotoka ku GPS', 'Abakuumi Abalina Ebyokulwanyisa', 'Okutambuza Eby’Omuwendo mu Nnyonyi'],
        },
        {
          serviceId: 'pillar-soc',
          targetAudience: 'Amakolero n’Ebizimbe Ebikulu',
          title: 'Kamera za AI Ez’Amangu n’Ekitebe kya SOC',
          description: 'Ekitebe ekikulu ekirondoola kamera za AI, ebyuma ebiwulira ebbugumu ly’omubiri, n’enduulu y’amangu saawa 24.',
          features: ['Kamera za AI Ezitegeera Amangu Obulabe', 'Ebyuma Ebikuuma Enkomerero', 'Okukwatagana mu Butikitiki Butono', 'Okuyita Poliisi Amangwago'],
        },
        {
          serviceId: 'pillar-industry',
          targetAudience: 'Eby’Ebyobugagga eby’omu Ttaka n’Amafuta',
          title: 'Obukuumi bw’Amakolero Amanene',
          description: 'Enteekateeka ey’omulembe ey’okukuuma ebyuma bya zaabu n’amakolero amanene okuva ku babbi.',
          features: ['Ebikomera eby’Ebyuma eby’Omulembe', 'Ebifo Ebikebera Abalina Okuyingira', 'Abasirikale Abali Awo Amangwago', 'Okukuuma Eby’Omuwendo Okutabula'],
        },
        {
          serviceId: 'pillar-audit',
          targetAudience: 'Amambasada, Ebitongole n’Abagagga',
          title: 'Okwekenneenya Obukuumi n’Amagezi ag’Enkizo',
          description: 'Okwekenneenya embeera y’obukuumi, okuzuula ebifo ebinafu, n’okukuuma abakungu ab’amanyi.',
          features: ['Okukebera Ebifo Ebinafu', 'Okuteekateeka Enguudo Ensaamu', 'Enteekateeka ey’Ebiseera eby’Akabi', 'Okussa mu Nkola Ebiragiro by’Amateeka'],
        },
      ],
    },
    tech: {
      heading: 'Tekinologiya w’Obukuumi n’Okulondoola kwa AI',
      coreMessage: 'Amagezi g’Omuntu + Obwengu bwa Tekinologiya = Obukuumi Obw’Amangu',
      items: [
        {
          metricBadge: '< Butikitiki 2.4 Okuzuula Obuzibu',
          title: 'Kamera za AI Ezitegeera Amangu',
          description: 'Kamera ezitegeera abantu abatambulatambula n’emmotoka ezitayina lukusa nga tewannabaawo bulumbaganyi.',
          bullets: ['Video etambula mu butikitiki butono', 'Okuzuula emmundu n’abantu', 'Okukuba enduulu mu kitebe kya SOC'],
        },
        {
          metricBadge: 'Okulondoola ku GPS 100%',
          title: 'Okulondoola Emmotoka za CIT ku GPS',
          description: 'Okumanya emmotoka weziri, embiro zaazo, enzigi obanga zigguddwawo, n’oluguudo mweziri saawa zonna.',
          bullets: ['Okulonda enguudo ezitalimu buzibu', 'Okuzikiza yingini ng’oli wala nga waliwo akabi', 'Pulaani y’amangu ey’okutaasa'],
        },
        {
          metricBadge: 'Tewali Kifo Kinnafu Kyokka',
          title: 'Okuggula n’Engalo z’Abantu Babiri',
          description: 'Okuyingira mu ssanduuko kyetaagisa alama z’engalo okuva ku basirikale babiri n’olukusa lwa SOC.',
          bullets: ['Alama z’engalo ez’abantu babiri', 'Ebiwandiiko ebitasobola kusangulwawo', 'Ebyuma ebiggalira ku ssaawa emanyiddwa'],
        },
        {
          metricBadge: 'Okubeera Obeera Bulindaala Saawa 24',
          title: 'Ekitebe Ekikulu ekya SOC',
          description: 'Ekitebe ekikulu ekitambuza pulogulaamu y’obukuumi bw’ebifo byonna n’emmotoka eziri ku kkubo.',
          bullets: ['Essimu ey’amangu eya Poliisi ya Uganda', 'Ebyuma eby’omu bbanga ebitafa', 'Okuddamu okwekenneenya video zonna'],
        },
      ],
    },
    industry: {
      heading: 'Obukuumi Obw’Enkizo mu Mirimu Egy’Obulabe',
      subheading: 'Enkola z’obukuumi ezikoleddwa okusinziira ku mbeera z’e Ggwanga n’Ebuvanjuba bwa Afrika.',
      items: [
        {
          name: 'Eby’Ebyobugagga eby’omu Ttaka ne Zaabu',
          risk: 'Akabi k’abazigu ab’emmundu n’obubbi ng’ebintu bisimbula okusala ensalo.',
          tags: ['Amasanduuko ga Zaabu', 'Emmotoka Ezitamenyeka eza CIT', 'Ebyuma Ebiwulira Ebbugumu'],
        },
        {
          name: 'Eby’Amafuta n’Omukka',
          risk: 'Ebibangirize by’emidumu gy’amafuta n’ebifo eby’ewala ennyo.',
          tags: ['Ebikomera eby’Omulembe', 'Drones Ezikuuma Ebbanga', 'Kamera z’Ekitebe kya SOC'],
        },
        {
          name: 'Bbanka n’Ebitongole by’Ensimbi',
          risk: 'Okulumba amatabi ga bbanka n’obubbi bw’amasanduuko g’ensimbi ekiro.',
          tags: ['Okukuuma Sente Ezitambuzibwa', 'Amasanduuko ga Class-4', 'Amasanduuko g’Ebyama'],
        },
        {
          name: 'Ebizimbe n’Amakampuni g’Enguudo (EPC)',
          risk: 'Okubba ebyuma ebizito n’okuyingira amaterekero g’ebikozesebwa.',
          tags: ['Ebifo Ebikuumibwa Saawa 24', 'GPS ku Mmotoka Zonna', 'Kamera za AI Ezikuuma'],
        },
        {
          name: 'Amakampuni, Amambasada n’Abapuliida',
          risk: 'Okubba ebiwandiiko eby’enkizo n’obulabe eri abakungu abakulu.',
          tags: ['Obukuumi bw’Abakungu', 'Amagezi ag’Obukuumi', 'Okutereka Ebiwandiiko mu Sanduuko'],
        },
        {
          name: 'Amaka g’Abagagga n’Abalina Eby’Omuwendo',
          risk: 'Abalumbaganyi abayingira mu maka n’okutulugunya ab’omumaka.',
          tags: ['Amasanduuko Ag’Obwannannyini', 'Ebisenge eby’Okwekwekamu', 'Enduulu ey’Amangu'],
        },
      ],
    },
    trust: {
      heading: 'Okutwesiga Kye Ky’Obugagga Kyaffe Ekikulu.',
      subheading: 'Bw’oba okuuma ebyapa by’ettaka oba bbanka enkulu, obwesigwa buvunaanyizibwa obutasobola kusuulibwa.',
      items: [
        {
          standard: 'Empisa z’Okukebera Abakozi',
          title: 'Okukebera Abakozi n’Obwegendereza',
          description: 'Buli musirikale n’omuvuzi w’emmotoka akeberwa ebikwata ku ngalo ze, emisango n’obulamu bwe.',
        },
        {
          standard: 'Enkola ey’Okukwasa Eby’Omuwendo',
          title: 'Okukwasa Eby’Omuwendo mu Mazima',
          description: 'Zaabu ne sente zonna ziweebwa ennamba ez’enjawulo n’okukwasibwa abantu babiri abawandiika.',
        },
        {
          standard: 'Enkola ey’Okukuumira Ebintu mu Kyama',
          title: 'Okukuumira Byonna mu Kyama',
          description: 'Endagaano ez’amanyi ez’okukuuma ebyama n’okukuuma ebiwandiiko awatali kuteekebwa ku mutimbagano.',
        },
        {
          standard: 'Layisinsi z’Amateeka g’e Ggwanga',
          title: 'Ezikkirizibwa mu Mateeka Ge Ggwanga',
          description: 'Zirambikiddwa Minisitule y’Ensonga z’Omunda era zikolera ku biragiro bya Bank of Uganda.',
        },
      ],
    },
    lead: {
      heading: 'Kuuma Emirimu Gyo n’Eby’Obugagga Byo',
      description: 'Yogera n’abakugu baffe ab’obukuumi mu Kampala okufuna okwekenneenya okw’ekyama.',
      officeTitle: 'Ekitebe Ekikulu mu Kampala n’Ekitebe kya SOC',
      officeLocation: 'Plot 42 Nakasero Hill Road, Kampala, Uganda',
      officePhone: '+256 414 000 000',
      officeEmail: 'inquiries@sovereign-security.ug',
      officeHours: 'Ekitebe Ekikuuma Saawa 24 (SOC)',
      labels: {
        name: 'Erinnya Lyo Lyonna *',
        company: 'Kampuni / Ekitongole',
        email: 'Email yo ey’Omulimu *',
        phone: 'Essimu yo ey’Amangu *',
        areaOfInquiry: 'Kiki Ky’Onoonya Okukuuma? *',
        scopeNotes: 'Ennyinnyonnyola ku By’Oyagala',
        consent: 'Nzikiriza okukozesa ebiwandiiko byange okusinziira ku mateeka g’obukuumi bw’ebikwata ku bantu.',
        submit: 'Saba Okwekenneenya Okw’Ekyama',
      },
      options: [
        'Amasanduuko g’Ebyama mu Bbanka',
        'Emmotoka Ezitambuza Sente ne Zaabu (CIT)',
        'Kamera za AI Ez’Amangu n’Ekitebe kya SOC',
        'Obukuumi bw’Amakolero n’Eby’Ebyobugagga',
        'Okwekenneenya Obukuumi n’Amagezi ag’Enkizo',
      ],
    },
  },

  'fr-FR': {
    hero: {
      eyebrow: 'Sécurité Institutionnelle et Protection d’Actifs',
      title: 'Protéger les Actifs Critiques. Sécuriser l’Avenir de l’Ouganda.',
      description: 'Des coffres-forts privés ultra-sécurisés au transport de fonds blindé (CIT) et à la surveillance IA 24/7 pour les secteurs stratégiques en Ouganda.',
      primaryCta: 'Demander une Évaluation',
      secondaryCta: 'Explorer les Coffres Privés',
      badges: ['Conforme Banque Centrale d’Ouganda', 'Centre SOC Certifié ISO 27001', 'Coffres Modulaires Classe-4', 'Flotte Blindée B6/B7'],
    },
    comparison: {
      heading: 'Le Changement de Paradigme dans la Défense d’Actifs',
      subheading: 'Pourquoi les institutions modernes privilégient la sécurité technologique proactive plutôt que le gardiennage statique.',
      legacyTitle: 'Modèle de Sécurité Traditionnel',
      legacyPoints: [
        'Gardes statiques sans communication en temps réel',
        'Registres manuels vulnérables aux falsifications',
        'Véhicules non blindés pour le transport d’or et de devises',
        'Intervention tardive en cas d’attaque coordonnée',
      ],
      modernTitle: 'Plateforme de Sécurité Intégrée',
      modernPoints: [
        'Coordination centrale SOC avec télémétrie vidéo sous 100ms',
        'Traçabilité cryptographique immuable pour chaque accès',
        'Flotte blindée B6/B7 avec analyse dynamique des itinéraires par IA',
        'Unités tactiques armées d’intervention rapide coordonnées',
      ],
    },
    services: {
      heading: 'Cinq Piliers Stratégiques de Services',
      subheading: 'Défense institutionnelle d’actifs et infrastructures de haute sécurité en Afrique de l’Est.',
      items: [
        {
          serviceId: 'pillar-custody',
          targetAudience: 'Mines, Banques et Patrimoines Privés',
          title: 'Garde Sécurisée et Coffres-Forts Privés',
          description: 'Coffres modulaires de classe 4 à Kampala avec accès biométrique sous double garde et couverture d’assurance complète.',
          features: ['Accès Biométrique sous Double Clé', 'Surveillance Capteurs 24/7', 'Assurance Complète', 'Coffres-Forts Individuels Confidentiels'],
        },
        {
          serviceId: 'pillar-cit',
          targetAudience: 'Raffineries, Commerce et Banques',
          title: 'Transport de Fonds et Métaux Précieux (CIT)',
          description: 'Transport blindé de devises et de métaux précieux à Kampala, Entebbe et sur les corridors miniers régionaux.',
          features: ['Flotte Blindée Balistique B6/B7', 'Optimisation GPS en Temps Réel', 'Escorte Tactique Armée', 'Transferts Aériens Scellés'],
        },
        {
          serviceId: 'pillar-soc',
          targetAudience: 'Industrie et Infrastructures Stratégiques',
          title: 'Surveillance par IA et Centre Opérationnel (SOC)',
          description: 'Centre opérationnel 24/7 intégrant caméras IA edge, capteurs thermiques périmétriques et alertes automatiques.',
          features: ['Détection d’Anomalies par IA Edge', 'Défense Périmétrique Thermique', 'Télémétrie d’Alerte sous 100ms', 'Intervention Rapide Coordonnée'],
        },
        {
          serviceId: 'pillar-industry',
          targetAudience: 'Concessions Minières et Pétrolières',
          title: 'Défense Industrielle et Sites à Haut Risque',
          description: 'Architecture intégrée de défense physique et électronique sur mesure pour les mines d’or et raffineries.',
          features: ['Grilles Périmétriques Anti-Intrusion', 'Postes de Contrôle Biométriques', 'Unités Tactiques sur Site', 'Prévention des Pertes Logistiques'],
        },
        {
          serviceId: 'pillar-audit',
          targetAudience: 'Entreprises, Ambassades et Investisseurs',
          title: 'Évaluation des Risques et Audits de Sécurité',
          description: 'Évaluations de haut niveau, audits de vulnérabilité physique et stratégies de protection rapprochée pour dirigeants.',
          features: ['Audits de Vulnérabilité de Sites', 'Profilage des Itinéraires Dirigeants', 'Architecture de Gestion de Crise', 'Conseil en Conformité Réglementaire'],
        },
      ],
    },
    tech: {
      heading: 'Moteur Technologique et Télémétrie IA',
      coreMessage: 'Intelligence Humaine + Intelligence Artificielle = Protection Supérieure',
      items: [
        {
          metricBadge: '< 2.4s Détection d’Incident',
          title: 'Vidéo IA Edge et Détection de Menaces',
          description: 'Détection en temps réel des intrusions périmétriques et arrêts de véhicules suspects avant toute tentative d’effraction.',
          bullets: ['Streaming vidéo sous 100ms', 'Détection automatique d’armes et silhouettes', 'Escalade d’alarme immédiate au SOC'],
        },
        {
          metricBadge: '100% Télémétrie Flotte Temps Réel',
          title: 'GPS et Télémétrie Blindée CIT',
          description: 'Suivi continu de la vitesse, de l’état des verrous de portes et des déviations de géorepérage des véhicules blindés.',
          bullets: ['Évaluation dynamique du risque routier', 'Coupe-circuit moteur à distance', 'Bouton d’urgence avec intervention tactique'],
        },
        {
          metricBadge: 'Zéro Point Unique de Défaillance',
          title: 'Accès Biométrique sous Double Garde',
          description: 'L’accès aux coffres nécessite la double validation biométrique de deux gardiens certifiés et l’autorisation du SOC.',
          bullets: ['Authentification biométrique double opérateur', 'Registres d’accès cryptographiques infalsifiables', 'Mécanismes d’interverrouillage temporisé'],
        },
        {
          metricBadge: 'Disponibilité Continue 24/7',
          title: 'Centre de Commandement et Déploiement SOC',
          description: 'Centre de commandement centralisé coordonnant alarmes fixes, escortes mobiles et unités tactiques d’élite.',
          bullets: ['Ligne directe Police d’Ouganda & secours privés', 'Liaison satellite et cellulaire redondante', 'Revue complète et audit des enregistrements'],
        },
      ],
    },
    industry: {
      heading: 'Défense Spécialisée pour Secteurs Stratégiques',
      subheading: 'Architectures de sécurité adaptées aux profils de vulnérabilité spécifiques en Ouganda et Afrique de l’Est.',
      items: [
        {
          name: 'Mines et Raffineries d’Or',
          risk: 'Risque élevé d’attaque à main armée et de détournement lors des transports transfrontaliers.',
          tags: ['Garde de Lingots', 'Logistique Blindée CIT', 'Capteurs Thermiques Périmétriques'],
        },
        {
          name: 'Secteur Pétrolier et Gazier',
          risk: 'Oléoducs stratégiques, plateformes isolées et corridors d’approvisionnement sensibles.',
          tags: ['Anti-Intrusion Périmétrique', 'Surveillance par Drones', 'Télémétrie SOC Distante'],
        },
        {
          name: 'Banques et Établissements Financiers',
          risk: 'Braquage d’agences, vulnérabilité du traitement des espèces et coffres nocturnes.',
          tags: ['Escorte de Devises', 'Coffres Modulaires Classe-4', 'Consignes Privées'],
        },
        {
          name: 'Infrastructures et Entreprises EPC',
          risk: 'Vol d’équipements lourds, sécurité des bases-vie et entrepôts de matériaux.',
          tags: ['Sites Sécurisés 24/7', 'Traceurs GPS Véhicules', 'Caméras Périmétriques IA'],
        },
        {
          name: 'Sièges d’Entreprises, Ambassades et Cabinets',
          risk: 'Espionnage économique, risques pour les dirigeants et documents confidentiels.',
          tags: ['Protection Exécutive', 'Audit des Risques', 'Coffres-Forts Scellés'],
        },
        {
          name: 'Clients Privés et Propriétés Familiales',
          risk: 'Intrusion à domicile, menaces ciblées et protection du patrimoine familial.',
          tags: ['Coffres Privés Sécurisés', 'Chambres Fortes Résidentielles', 'Alarme d’Urgence'],
        },
      ],
    },
    trust: {
      heading: 'La Confiance est Notre Actif Principal.',
      subheading: 'Pour la garde de titres fonciers, d’infrastructures nationales ou de réserves bancaires, la confiance doit être absolue.',
      items: [
        {
          standard: 'Standard de Recrutement',
          title: 'Filtrage Rigoureux du Personnel',
          description: 'Chaque agent de sécurité, gardien de coffre et chauffeur subit des vérifications biométriques, criminelles et financières continues.',
        },
        {
          standard: 'Standard de Chaîne de Garde',
          title: 'Chaîne de Responsabilité Stricte',
          description: 'Tout transfert d’or ou de devises fait l’objet d’un scellé numéroté, d’un double contrôle physique et d’un enregistrement numérique.',
        },
        {
          standard: 'Standard de Confidentialité',
          title: 'Confidentialité par Conception',
          description: 'Accords stricts de non-divulgation, conservation des données hors-ligne et compartimentation rigoureuse des accès.',
        },
        {
          standard: 'Standard d’Agrément Officiel',
          title: 'Agréé et Réglementé',
          description: 'Certifié par le Ministère des Affaires Intérieures et conforme aux directives de transport de la Banque Centrale d’Ouganda.',
        },
      ],
    },
    lead: {
      heading: 'Sécurisez Vos Opérations et Vos Actifs',
      description: 'Échangez avec nos spécialistes basés à Kampala pour une évaluation confidentielle de vos besoins de sécurité.',
      officeTitle: 'Siège Social et Centre SOC de Kampala',
      officeLocation: 'Plot 42 Nakasero Hill Road, Kampala, Ouganda',
      officePhone: '+256 414 000 000',
      officeEmail: 'inquiries@sovereign-security.ug',
      officeHours: 'Centre Opérationnel de Sécurité 24/7 (SOC)',
      labels: {
        name: 'Nom et Prénom *',
        company: 'Société / Institution',
        email: 'Adresse Email Professionnelle *',
        phone: 'Numéro de Téléphone Direct *',
        areaOfInquiry: 'Domaine Principal de Demande *',
        scopeNotes: 'Périmètre d’Opération / Spécifications',
        consent: 'J’accepte le traitement confidentiel de mes données conformément aux réglementations de protection de la vie privée.',
        submit: 'Transmettre la Demande Confidentielle',
      },
      options: [
        'Garde Sécurisée et Coffres-Forts Privés',
        'Transport de Fonds et Métaux Précieux (CIT)',
        'Surveillance par IA et Centre Opérationnel (SOC)',
        'Défense Minière et Concessions Industrielles',
        'Évaluation des Risques et Audits de Sécurité',
      ],
    },
  },

  'ar-SA': {
    hero: {
      eyebrow: 'الأمن المؤسسي وحماية الأصول الاستراتيجية',
      title: 'حماية الأصول الحيوية. تأمين مستقبل أوغندا.',
      description: 'من خزائن الودائع المصرفية الخاصة وفائقة الأمان إلى لوجستيات السبائك المصفحة وحراسة الذكاء الاصطناعي على مدار الساعة.',
      primaryCta: 'طلب تقييم أمني',
      secondaryCta: 'استكشاف الخزائن الخاصة',
      badges: ['معتمد من بنك أوغندا المركزي', 'مركز عمليات معتمد ISO 27001', 'خزائن حصينة فئة 4', 'أسطول مصفح B6/B7'],
    },
    comparison: {
      heading: 'التحول الجذري في حماية الأصول',
      subheading: 'لماذا تختار المؤسسات الحديثة الأنظمة الأمنية الذكية بدلاً من الحراسة التقليدية الثابتة.',
      legacyTitle: 'نموذج الحراسة التقليدي',
      legacyPoints: [
        'حراس أمن ثابتون دون اتصال ومتابعة لحظية',
        'سجلات ورقية يدوية عرضة للتلاعب والضياع',
        'مركبات غير مصفحة لنقل الأموال والسبائك الثمينة',
        'تأخر استجابة الطوارئ أثناء الحوادث المنظمة',
      ],
      modernTitle: 'منظومة الدفاع المتكاملة',
      modernPoints: [
        'غرفة تحكم مركزية متصلة بالفيديو في أقل من 100 ميلي ثانية',
        'سجلات تدقيق مشفرة غير قابلة للتعديل لكل عملية دخول',
        'أسطول مصفح ضد الرصاص B6/B7 مدعوم بالذكاء الاصطناعي',
        'فرق استجابة تكتيكية مسلحة منسقة مع الأجهزة الرسمية',
      ],
    },
    services: {
      heading: 'خمس ركائز استراتيجية للخدمات الأمنية',
      subheading: 'حماية مؤسسية شاملة للأصول وبنية تحتية أمنية متقدمة في شرق أفريقيا.',
      items: [
        {
          serviceId: 'pillar-custody',
          targetAudience: 'المناجم، البنوك، وكبار المستثمرين',
          title: 'الحفظ الآمن والخزائن الخاصة',
          description: 'خزائن مصرفية حصينة فئة 4 في كمبالا مع ميزة الدخول الحيوي المشترك وتغطية تأمينية شاملة.',
          features: ['دخول بالبصمة الحيوية الثنائية', 'مراقبة حساسة على مدار 24 ساعة', 'تغطية تأمينية شاملة', 'صناديق أمانات سرية وفائقة الحماية'],
        },
        {
          serviceId: 'pillar-cit',
          targetAudience: 'مصافي المعادن والقطاع المالي',
          title: 'نقل الأموال والسبائك المصفح (CIT)',
          description: 'نقل مصفح للعملات والمعادن الثمينة في كمبالا وعنتيبي وممرات التعدين الرئيسية.',
          features: ['أسطول مصفح بالستياً B6/B7', 'تتبع الأقمار الصناعية GPS لحظياً', 'مرافقة تكتيكية مسلحة', 'نقل جوي مؤمن بين الخزائن'],
        },
        {
          serviceId: 'pillar-soc',
          targetAudience: 'المنشآت الصناعية والحيوية',
          title: 'المراقبة بالذكاء الاصطناعي ومركز العمليات (SOC)',
          description: 'مركز عمليات متكامل على مدار 24 ساعة يربط كاميرات الذكاء الاصطناعي وأجهزة الاستشعار الحرارية.',
          features: ['رصد التهديدات عبر خوارزميات AI', 'حماية حرارية لحدود المنشأة', 'إرسال التنبيهات في أقل من 100ms', 'استجابة سريعة وتدخل مباشر'],
        },
        {
          serviceId: 'pillar-industry',
          targetAudience: 'امتيازات التعدين والنفط',
          title: 'أمن القطاعات الصناعية والتعدين',
          description: 'منظومة حماية متكاملة مصممة خصيصاً لمناجم الذهب والمصافي والمصانع الكبرى.',
          features: ['أسوار مانعة للاختراق', 'نقاط تفتيش بيومترية متطورة', 'فرق استجابة تكتيكية ميدانية', 'حماية سلاسل الإمداد من الفقدان'],
        },
        {
          serviceId: 'pillar-audit',
          targetAudience: 'الشركات، السفارات، والمستثمرون',
          title: 'تقييم المخاطر والاستشارات الأمنية',
          description: 'تقييمات استخباراتية رفيعة المستوى، وتدقيق الثغرات، وخطط حماية الشخصيات التنفيذية.',
          features: ['تدقيق الثغرات الأمنية للمواقع', 'تخطيط مسارات كبار الشخصيات', 'إدارة الأزمات والخطط الطارئة', 'الاستشارات والامتثال القانوني'],
        },
      ],
    },
    tech: {
      heading: 'محرك التقنية والتتبع الذكي',
      coreMessage: 'الخبرة البشرية + الذكاء الاصطناعي = حماية أذكى وأسرع',
      items: [
        {
          metricBadge: '< 2.4 ثانية لرصد الحدث',
          title: 'كاميرات الذكاء الاصطناعي وعزل التهديد',
          description: 'تكتشف الكاميرات الذكية التحركات المشبوهة وتوقف المركبات غير المصرح بها قبل حدوث الاختراق.',
          bullets: ['بث الفيديو في أقل من 100 ميلي ثانية', 'التعرف الآلي على الأسلحة والأشخاص', 'تصعيد الإنذار فوراً لمركز العمليات'],
        },
        {
          metricBadge: 'تتبع لحظي 100% للأسطول',
          title: 'تتبع مركبات CIT عبر GPS',
          description: 'متابعة مباشرة لسرعة المركبات المصفحة، وحالة الأبواب، والانحراف عن المسارات المحددة.',
          bullets: ['تقييم المخاطر ديناميكياً على الطريق', 'إمكانية إيقاف المحرك عن بُعد عند الطوارئ', 'أزرار استغاثة مع استجابة تكتيكية فورية'],
        },
        {
          metricBadge: 'أمان بيومتري مضاعف',
          title: 'الدخول الحيوي المزدوج',
          description: 'يتطلب فتح الخزينة مصادقة بصمة حيوية من مسؤولَين معتمدَين مع إذن مركز العمليات.',
          bullets: ['مصادقة ثنائية متعددة العوامل', 'سجلات وصول مشفرة وغير قابلة للتعديل', 'أقفال زمنية مبرمجة'],
        },
        {
          metricBadge: 'جاهزية تامة 24/7',
          title: 'غرفة القيادة والسيطرة SOC',
          description: 'مركز قيادة موحد ينسق إنذارات المواقع، والمواكب المتحركة، والفرق التكتيكية.',
          bullets: ['خط اتصال مباشر مع الشرطة الأوغندية', 'اتصال احتياطي عبر الأقمار الصناعية', 'مراجعة وتوثيق كامل لكافة التسجيلات'],
        },
      ],
    },
    industry: {
      heading: 'حماية متخصصة للقطاعات الحيوية',
      subheading: 'منظومات أمنية مخصصة للتعامل مع التحديات الأمنية في أوغندا وشرق أفريقيا.',
      items: [
        {
          name: 'المناجم ومصافي الذهب',
          risk: 'مخاطر السطو المسلح والسرقة والاعتداء أثناء النقل عبر الحدود.',
          tags: ['حفظ السبائك', 'لوجستيات النقل المصفح', 'حساسات حرارية للحدود'],
        },
        {
          name: 'قطاع النفط والغاز',
          risk: 'البنية التحتية لخطوط الأنابيب، والمواقع النائية، وسلاسل الإمداد الحيوية.',
          tags: ['حماية المحيط', 'مراقبة بالطائرات المسيرة', 'تتبع مركز SOC عن بعد'],
        },
        {
          name: 'البنوك والمؤسسات المالية',
          risk: 'السطو على الفروع، وثغرات معالجة النقد، وتأمين الخزائن الليلية.',
          tags: ['مرافقة الأموال يومياً', 'خزائن حصينة فئة 4', 'صناديق أمانات خاصة'],
        },
        {
          name: 'شركات المقاولات والبنية التحتية',
          risk: 'سرقة المعدات الثقيلة، وتأمين المجمعات السكنية النائية والمستودعات.',
          tags: ['حراسة المواقع 24/7', 'أجهزة تتبع GPS للمعدات', 'كاميرات ذكية للمحيط'],
        },
        {
          name: 'الشركات، السفارات، والهيئات القانونية',
          risk: 'التجسس التجاري، وسلامة كبار المسؤولين، وحماية الوثائق السرية.',
          tags: ['حماية الشخصيات', 'استشارات تقييم المخاطر', 'حفظ الوثائق المشفرة'],
        },
        {
          name: 'العملاء والشخصيات البارزة',
          risk: 'اقتحام المنازل، والتهديدات المستهدفة، وحماية ممتلكات العائلة.',
          tags: ['خزائن خاصة بالمنازل', 'غرف طوارئ محصنة', 'استجابة فورية للإنذار'],
        },
      ],
    },
    trust: {
      heading: 'الثقة هي أثمن أصولنا.',
      subheading: 'عند حماية صكوك الأراضي أو البنية التحتية الوطنية أو خزائن البنوك، يجب أن تكون الثقة مطلقة وقابلة للتدقيق.',
      items: [
        {
          standard: 'معيار الفحص الأمني للكوادر',
          title: 'تدقيق أمني صارم للكوادر',
          description: 'يخضع جميع حراس الأمن وسائقي المركبات المصفحة لفحص جنائي وبيومتري ومالي دائم.',
        },
        {
          standard: 'معيار سلسلة الحفظ والتسليم',
          title: 'سلسلة تسليم موثقة ودقيقة',
          description: 'تخضع جميع شحنات الذهب والأموال لترقيم مشفر، وتسليم ثنائي، وتوثيق رقمي مشفر.',
        },
        {
          standard: 'معيار السرية المؤسسية',
          title: 'السرية التامة في التصميم',
          description: 'اتفاقيات صارمة لعدم الإفصاح، وحفظ البيانات بأنظمة معزولة تماماً عن الإنترنت.',
        },
        {
          standard: 'معيار التراخيص الحكومية',
          title: 'مرخص ومعتمد رسمياً',
          description: 'معتمد من وزارة الشؤون الداخلية وملتزم بتعليمات بنك أوغندا المركزي لنقل الأصول.',
        },
      ],
    },
    lead: {
      heading: 'تأمين عملياتك وأصولك الاستراتيجية',
      description: 'تواصل مع خبرائنا في كمبالا لإجراء تقييم أمني سري وشامل لمؤسستك.',
      officeTitle: 'المقر الرئيسي ومركز القيادة SOC في كمبالا',
      officeLocation: 'Plot 42 Nakasero Hill Road, Kampala, Uganda',
      officePhone: '+256 414 000 000',
      officeEmail: 'inquiries@sovereign-security.ug',
      officeHours: 'مركز العمليات الأمنية على مدار الساعة (SOC)',
      labels: {
        name: 'الاسم الكامل *',
        company: 'الشركة / المؤسسة',
        email: 'البريد الإلكتروني الرسمي *',
        phone: 'رقم الهاتف المباشر *',
        areaOfInquiry: 'مجال الاستفسار الرئيسي *',
        scopeNotes: 'نطاق العمليات / المتطلبات الخاصة',
        consent: 'أوافق على معالجة البيانات بسرية تامة ووفقاً للوائح حماية البيانات الوطنية.',
        submit: 'إرسال الطلب السري',
      },
      options: [
        'الحفظ الآمن والخزائن المصرفية الخاصة',
        'نقل الأموال والسبائك المصفح (CIT)',
        'المراقبة بالذكاء الاصطناعي ومركز SOC',
        'حماية المناجم والمنشآت الصناعية',
        'تقييم المخاطر والاستشارات الأمنية',
      ],
    },
  },

  'zh-CN': {
    hero: {
      eyebrow: '机构级安防与战略资产保护',
      title: '守护关键资产，捍卫乌干达经济未来。',
      description: '从银行级私人高安金库保管、装甲贵金属与现钞押运，到24/7全天候人工智能安防指挥中心。',
      primaryCta: '申请安全评估',
      secondaryCta: '了解私人金库',
      badges: ['符合乌干达央行监管标准', 'ISO 27001认证SOC中心', 'Class-4级模块化金库', 'B6/B7级重装防弹车队'],
    },
    comparison: {
      heading: '资产防务的范式革新',
      subheading: '为什么现代机构选择技术驱动的主动安防，而非传统的被动静态安保。',
      legacyTitle: '传统安保模式',
      legacyPoints: [
        '固定岗哨缺乏实时协同与应急响应通讯',
        '纸质手写登记簿易被篡改或遗失',
        '使用普通非防弹车辆运送现金与贵重金属',
        '遭遇协同袭击时应急响应严重滞后',
      ],
      modernTitle: '一体化智能安防平台',
      modernPoints: [
        '集中式SOC指挥中心，毫秒级视频遥测直连',
        '每次出入均具备不可篡改的加密审计追踪',
        'B6/B7重装防弹车队配备AI动态路线风险规划',
        '多部门协同的武装快速反应战术分队',
      ],
    },
    services: {
      heading: '五大核心战略业务支柱',
      subheading: '为东非地区的机构客户提供全方位的资产防务与高等级安保基础设施。',
      items: [
        {
          serviceId: 'pillar-custody',
          targetAudience: '矿业公司、金融机构及高净值家族',
          title: '安全托管与私人金库',
          description: '位于坎帕拉的Class-4级模块化高安金库，支持双人生物识别联控，并提供全面保险保障。',
          features: ['生物识别双人联控验证', '24/7全方位传感器监控', '承保机构全额险种覆盖', '高度保密的私人贵宾保险箱'],
        },
        {
          serviceId: 'pillar-cit',
          targetAudience: '贵金属精炼厂及商业银行',
          title: '武装押运与贵金属物流 (CIT)',
          description: '覆盖坎帕拉、恩德培机场及主要矿区的重装现金与贵金属武装押运物流。',
          features: ['B6/B7高等级防弹装甲车队', '实时GPS动态最优路径规划', '全副武装的战术护送特勤分队', '金库间密封航空直达移交'],
        },
        {
          serviceId: 'pillar-soc',
          targetAudience: '大型工业区与关键基础设施',
          title: 'AI智能监控与集中指挥中心 (SOC)',
          description: '24/7全天候集中式安防指挥中心，融合边缘AI视频分析、热成像周界防范及自动报警联动。',
          features: ['边缘AI异常行为实时识别', '热成像周界入侵智能阻击', '低于100毫秒的报警遥测流', '警务与快速反应分队即时联动'],
        },
        {
          serviceId: 'pillar-industry',
          targetAudience: '特许矿区与油气基础设施',
          title: '重大工业与矿产特许权安防',
          description: '为金矿、精炼厂及重工业制造基地量身打造的物防与技防一体化交钥匙安保体系。',
          features: ['高等级周界智能防入侵网格', '多模态生物识别通行关卡', '驻地全天候武装快反分队', '供应链全流程防损与风控'],
        },
        {
          serviceId: 'pillar-audit',
          targetAudience: '跨国企业、使领馆及涉外投资者',
          title: '威胁评估与安全审计咨询',
          description: '高级别安全情报评估、物理设施漏洞渗透审计及高级管理层要人保护方案。',
          features: ['营地与设施物理脆弱性审计', '高管出行动态风险路线规划', '危机应急响应与撤离架构', '涉外法律与政府合规顾问'],
        },
      ],
    },
    tech: {
      heading: '安防技术引擎与AI遥测中枢',
      coreMessage: '专业人类智慧 + 人工智能中枢 = 更智能、更迅速的防护能力',
      items: [
        {
          metricBadge: '< 2.4秒 威胁精准锁定',
          title: 'AI边缘视频分析与威胁隔离',
          description: '在发生入侵之前，智能边缘相机即可精准识别非法徘徊、翻越周界及未授权车辆停留。',
          bullets: ['低于100ms的实时视频遥测回传', '枪械与可疑人员轮廓自动甄别', '即时向SOC中枢升级最高级告警'],
        },
        {
          metricBadge: '100% 全程车队动态遥测',
          title: 'CIT装甲车队GPS智能管控',
          description: '全程持续监控装甲车辆的行驶速度、门锁状态、发动机工况及电子围栏偏离。',
          bullets: ['沿途风险动态实时评估', '紧急情况下远程安全切断动力', '一键求助直连武装快反分队'],
        },
        {
          metricBadge: '杜绝任何单点安全风险',
          title: '生物识别与双人联控机制',
          description: '进入金库必须由两名认证库管员同时进行生物识别验证，并经SOC中枢远程二次核准。',
          bullets: ['双人生物识别多因子联合认证', '不可篡改的加密数字出入审计日志', '防尾随与延时互锁电子门禁'],
        },
        {
          metricBadge: '24/7 全天候待命响应',
          title: '24/7 SOC指挥与快速出警中枢',
          description: '集中的指挥调度大脑，统一协同固定安防报警、押运护送及战术快反分队。',
          bullets: ['直通乌干达警方与特勤的专线', '卫星与多蜂窝网络冗余无缝备份', '全量音视频回放与事件复盘系统'],
        },
      ],
    },
    industry: {
      heading: '面向重点高危行业的专项防务',
      subheading: '针对乌干达及东非特定行业的脆弱性风险，量身打造针对性安防架构。',
      items: [
        {
          name: '矿产开采与黄金精炼',
          risk: '跨境运输与仓储环节面临武装抢劫、内盗及沿途伏击等重大风险。',
          tags: ['黄金安全托管', '装甲CIT武装押运', '周界热成像预警'],
        },
        {
          name: '石油与天然气行业',
          risk: '长输管线基础设施、偏远油气井场及危险品运输供应链安全。',
          tags: ['周界智能防入侵', '无人机巡检防务', '远程SOC遥测联动'],
        },
        {
          name: '商业银行与金融机构',
          risk: '营业网点抢劫、现金清分交接漏洞及夜间金库保管风险。',
          tags: ['每日现钞武装护送', 'Class-4级模块金库', '双控私人保管箱'],
        },
        {
          name: '大型工程与EPC承包商',
          risk: '重型施工机械盗窃、偏远营地安全及物资仓库入侵防范。',
          tags: ['24/7驻地武装封闭守护', '机械设备GPS智能定位', 'AI周界智能告警'],
        },
        {
          name: '外资企业、使领馆及法律机构',
          risk: '商业机密泄露、高管绑架敲诈风险及绝密商业文件保管。',
          tags: ['要人随身特勤护卫', '安全风险评估咨询', '高密绝密文件保险箱'],
        },
        {
          name: '私人家族与高端庄园',
          risk: '住宅入室抢劫、针对性人身威胁及贵重家族资产保护。',
          tags: ['私人高安保险箱', '安全屋一体化建造', '一键紧急报警快反'],
        },
      ],
    },
    trust: {
      heading: '信誉是我们最宝贵的核心资产。',
      subheading: '在保护土地产权、国家战略资产或企业金库时，信任必须是制度化、可审计且绝对可靠的。',
      items: [
        {
          standard: '特勤人员政审标准',
          title: '严苛的背景审查与持续品控',
          description: '所有特勤安保人员、金库管员和特种驾驶员均须通过持续的生物特征、无犯罪记录及财务信用审查。',
        },
        {
          standard: '资产监管移交标准',
          title: '严格的监管链与交接程序',
          description: '所有贵金属和现金流转均附带防篡改唯一数字封条、双人现场核验与全程加密数字登记。',
        },
        {
          standard: '机构保密合规标准',
          title: '原生级别的商业保密架构',
          description: '银行级保密协议、物理物理隔绝（Air-Gapped）数据留存及物理分区严密权限管控。',
        },
        {
          standard: '政府特许合规标准',
          title: '资质齐全受政府严格监管',
          description: '持有乌干达内政部颁发的高级特许牌照，全面符合乌干达央行高价值资产流转规范。',
        },
      ],
    },
    lead: {
      heading: '全面守护您的关键运营与核心资产',
      description: '欢迎联系我们在坎帕拉的安全与资产托管专家，获取保密的风险评估与方案定制。',
      officeTitle: '坎帕拉总部与SOC安防指挥中心',
      officeLocation: 'Plot 42 Nakasero Hill Road, Kampala, Uganda',
      officePhone: '+256 414 000 000',
      officeEmail: 'inquiries@sovereign-security.ug',
      officeHours: '24/7全天候安全运营指挥中心 (SOC)',
      labels: {
        name: '您的姓名 *',
        company: '所属企业 / 机构名称',
        email: '官方业务邮箱 *',
        phone: '直接联系电话 *',
        areaOfInquiry: '主要咨询业务领域 *',
        scopeNotes: '业务范围 / 特殊需求简要说明',
        consent: '我同意根据国家数据保护与隐私法规对本次安全咨询进行严格保密处理。',
        submit: '提交保密安全咨询申请',
      },
      options: [
        '私人高安金库与保管箱服务',
        '装甲现金与贵金属武装押运 (CIT)',
        'AI智能监控与SOC集中指挥中心',
        '矿业与重大工业特许权安防',
        '威胁风险评估与高级安全审计',
      ],
    },
  },
};

export class SemanticLandingTranslator {
  /**
   * Applies fully localized semantic content copy across all 7 sections of a CmsPageTree.
   */
  public static projectLocalizedPageTree(baseTree: CmsPageTree, locale: SupportedLocale): CmsPageTree {
    const copy = SEVEN_SECTION_LOCALIZATIONS[locale] || SEVEN_SECTION_LOCALIZATIONS['en-US'];

    const translatedSections = baseTree.sections.map((section) => {
      const translatedBlocks = section.blocks.map((block) => {
        switch (block.type) {
          case 'hero_banner': {
            const h = block as CmsHeroBannerBlock;
            return {
              ...h,
              payload: {
                ...h.payload,
                eyebrow: copy.hero.eyebrow,
                title: copy.hero.title,
                description: copy.hero.description,
                primaryCta: h.payload.primaryCta ? { ...h.payload.primaryCta, label: copy.hero.primaryCta } : undefined,
                secondaryCta: h.payload.secondaryCta ? { ...h.payload.secondaryCta, label: copy.hero.secondaryCta } : undefined,
                trustBadges: copy.hero.badges,
              },
            };
          }

          case 'comparison_matrix': {
            const c = block as CmsComparisonMatrixBlock;
            return {
              ...c,
              payload: {
                ...c.payload,
                sectionHeading: copy.comparison.heading,
                sectionSubheading: copy.comparison.subheading,
                legacyTitle: copy.comparison.legacyTitle,
                legacyPoints: copy.comparison.legacyPoints,
                modernTitle: copy.comparison.modernTitle,
                modernPoints: copy.comparison.modernPoints,
              },
            };
          }

          case 'service_catalog': {
            const s = block as CmsServiceCatalogBlock;
            const updatedServices = s.payload.services.map((item, idx) => {
              const locItem = copy.services.items[idx] || copy.services.items[0];
              return {
                ...item,
                targetAudience: locItem.targetAudience,
                serviceTitle: locItem.title,
                shortDescription: locItem.description,
                featureList: locItem.features,
              };
            });

            return {
              ...s,
              payload: {
                ...s.payload,
                sectionHeading: copy.services.heading,
                sectionSubheading: copy.services.subheading,
                services: updatedServices,
              },
            };
          }

          case 'tech_engine': {
            const t = block as CmsTechEngineBlock;
            const updatedTech = t.payload.technologies.map((item, idx) => {
              const locItem = copy.tech.items[idx] || copy.tech.items[0];
              return {
                ...item,
                metricBadge: locItem.metricBadge,
                featureTitle: locItem.title,
                description: locItem.description,
                bulletPoints: locItem.bullets,
              };
            });

            return {
              ...t,
              payload: {
                ...t.payload,
                sectionHeading: copy.tech.heading,
                coreMessage: copy.tech.coreMessage,
                technologies: updatedTech,
              },
            };
          }

          case 'industry_matrix': {
            const ind = block as CmsIndustryMatrixBlock;
            const updatedInd = ind.payload.industries.map((item, idx) => {
              const locItem = copy.industry.items[idx] || copy.industry.items[0];
              return {
                ...item,
                industryName: locItem.name,
                riskProfileSummary: locItem.risk,
                applicableServices: locItem.tags,
              };
            });

            return {
              ...ind,
              payload: {
                ...ind.payload,
                sectionHeading: copy.industry.heading,
                sectionSubheading: copy.industry.subheading,
                industries: updatedInd,
              },
            };
          }

          case 'trust_audit': {
            const tr = block as CmsTrustAuditBlock;
            const p = tr.payload as any;
            const updatedPillars = (p.pillars || []).map((item: any, idx: number) => {
              const locItem = copy.trust.items[idx] || copy.trust.items[0];
              return {
                ...item,
                standardBadge: locItem.standard,
                title: locItem.title,
                description: locItem.description,
              };
            });

            return {
              ...tr,
              payload: {
                ...p,
                sectionHeading: copy.trust.heading,
                sectionSubheading: copy.trust.subheading,
                pillars: updatedPillars,
              },
            };
          }

          case 'lead_capture_form': {
            const l = block as CmsLeadCaptureFormBlock;
            const p = l.payload as any;
            const updatedOffices = (p.officeLocations || (p.officeDetails ? [p.officeDetails] : [])).map((off: any) => ({
              ...off,
              city: copy.lead.officeTitle,
              address: copy.lead.officeLocation,
              phone: copy.lead.officePhone,
              contactEmail: copy.lead.officeEmail,
              operationalHours: copy.lead.officeHours,
            }));

            return {
              ...l,
              payload: {
                ...p,
                headline: copy.lead.heading,
                subheadline: copy.lead.description,
                submitButtonLabel: copy.lead.labels.submit,
                officeLocations: updatedOffices,
                localizedLabels: copy.lead.labels,
                areaOfInquiryOptions: copy.lead.options,
              },
            };
          }

          default:
            return block;
        }
      });

      return {
        ...section,
        blocks: translatedBlocks,
      };
    });

    return {
      ...baseTree,
      sections: translatedSections,
    };
  }
}
